import { Chart } from 'chart.js'
import { ChartData } from '../analytics-types'
import { defineComponent } from 'vue'

interface ChartDownloadMixinData {
  chart: Chart | null
}

export default defineComponent({
  props: {
    data: {
      type: Object as () => ChartData,
      required: true
    },
    downloadFileName: {
      type: String,
      default: 'chart'
    }
  },
  data(): ChartDownloadMixinData {
    return {
      chart: null
    }
  },
  methods: {
    downloadChart() {
      if (!this.chart) return;
      
      // Create a temporary canvas with white background
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = this.chart.canvas.width;
      tempCanvas.height = this.chart.canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      
      if (!tempCtx) return;
      
      // Fill with white background
      tempCtx.fillStyle = 'white';
      tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
      
      // Draw the chart on top
      tempCtx.drawImage(this.chart.canvas, 0, 0);
      
      const link = document.createElement('a');
      const filename = `${this.downloadFileName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-image.png`;
      link.download = filename;
      link.href = tempCanvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    downloadCSV() {
      if (!this.data) return;

      // Create CSV content with multiple datasets
      const headers = ['Label'];
      
      // Add dataset labels as headers
      this.data.datasets.forEach(dataset => {
        headers.push(dataset.label || 'Value');
        if (dataset.valueCounts) {
          headers.push('Count');
        }
      });

      // Create rows with all dataset values
      const rows = this.data.labels.map((label, index) => {
        const row = [label];
        
        // Add values and counts for each dataset
        this.data.datasets.forEach(dataset => {
          const value = dataset.data[index];
          row.push(String(value));
          
          if (dataset.valueCounts) {
            row.push(String(dataset.valueCounts[index] || 0));
          }
        });
        
        return row;
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const filename = `${this.downloadFileName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-data.csv`;
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }
  }
}) 